<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
import { RouterLink, RouterView, useRoute, useRouter } from "vue-router";
import { api, setActAs, type ActAsTarget } from "./api";
import { useSession } from "./stores/session";
import { useConversations } from "./stores/conversations";
import { useAdminCounts } from "./stores/adminCounts";
import { signOut } from "./auth";
import RenameNotice from "./components/RenameNotice.vue";
import VelmaWordmark from "./components/VelmaWordmark.vue";

const route = useRoute();
const router = useRouter();
const session = useSession();
const conversations = useConversations();
const adminCounts = useAdminCounts();

/** Sign-in and signed-out render bare -- no shell, no nav, no session. */
const isPublic = computed(() => route.meta.public === true);

// ---- Resizable nav sidebar ----
//
// <wa-page>'s --menu-width is normally a fixed value set in styles.css (15rem); dragging
// nav-resize-handle overrides it inline instead, in px, remembered across visits. isDesktop
// mirrors <wa-page>'s own default mobile-breakpoint (768px, unset in the template below) --
// below it the sidebar becomes a drawer, where a fixed width no longer means anything.
const NAV_WIDTH_KEY = "cortex.navWidth";
const NAV_WIDTH_MIN = 180;
const NAV_WIDTH_MAX = 480;
const NAV_WIDTH_DEFAULT = 240;

function clampNavWidth(px: number): number {
  return Math.min(NAV_WIDTH_MAX, Math.max(NAV_WIDTH_MIN, px));
}

function loadNavWidth(): number {
  const stored = Number(localStorage.getItem(NAV_WIDTH_KEY));
  return stored > 0 ? clampNavWidth(stored) : NAV_WIDTH_DEFAULT;
}

const navWidth = ref(loadNavWidth());
const isDesktop = ref(window.innerWidth >= 768);

function handleWindowResize() {
  isDesktop.value = window.innerWidth >= 768;
}

function startNavResize(e: PointerEvent) {
  e.preventDefault();
  const startX = e.clientX;
  const startWidth = navWidth.value;
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";

  function onMove(ev: PointerEvent) {
    navWidth.value = clampNavWidth(startWidth + (ev.clientX - startX));
  }
  function onUp() {
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    localStorage.setItem(NAV_WIDTH_KEY, String(navWidth.value));
  }
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
}

onMounted(() => window.addEventListener("resize", handleWindowResize));
onBeforeUnmount(() => window.removeEventListener("resize", handleWindowResize));

// The nav's conversation tree needs the list as soon as the shell renders -- which only happens
// after the route guard has confirmed a sign-in, so this never fires unauthenticated.
watchEffect(() => {
  if (!isPublic.value && route.name && !conversations.loaded) conversations.load();
  // Refetched on every navigation (not gated behind a "loaded" flag like the list above) so the
  // badges stay accurate after an admin/manager approves/denies a request on the pages they gate.
  if (!isPublic.value && route.name && (session.isAdmin || session.isManager)) adminCounts.load();
});

async function removeConversation(id: string) {
  await conversations.remove(id);
  // Removing the conversation being read leaves a dead URL; fall back to a fresh chat.
  if (route.params.id === id) await router.push({ name: "chat" });
}

const initials = computed(() =>
  session.displayName
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join(""),
);

const actAsTargets = ref<ActAsTarget[]>([]);

watch(
  () => session.realIsAdmin,
  async (yes) => {
    if (!yes) {
      actAsTargets.value = [];
      return;
    }
    try {
      actAsTargets.value = await api.actAsTargets();
    } catch {
      actAsTargets.value = [];
    }
  },
  { immediate: true },
);

async function onActAsChange(event: Event) {
  const email = ((event.target as HTMLSelectElement).value || "").trim() || null;
  setActAs(email);
  await session.reload();
  if (session.isAdmin || session.isManager) adminCounts.load();
  if (route.meta.admin && !route.meta.managerAllowed && !session.isAdmin) {
    await router.push({ name: "assigned" });
  }
}

async function stopActingAs() {
  setActAs(null);
  await session.reload();
  if (session.isAdmin || session.isManager) adminCounts.load();
}
</script>

<template>
  <RouterView v-if="isPublic" />

  <!--
    <wa-page> is the grid, the sticky regions and the responsive nav. The nav is written ONCE in
    slot="navigation": the component renders it as a desktop sidebar and moves it into a drawer on
    mobile, so duplicating it into the header would show it twice.

    The v-else-if waits for the first route resolution, so the shell doesn't flash before the
    guard decides between it and the sign-in page.
  -->
  <template v-else-if="route.name">
  <wa-page :style="isDesktop ? { '--menu-width': navWidth + 'px' } : undefined">
    <!-- <wa-page> hides this slot entirely when nothing is slotted into it, so once
         RenameNotice passes its cutoff date the strip leaves no gap above the header. -->
    <RenameNotice slot="banner" />

    <header slot="header" class="page-header wa-split">
      <div class="wa-cluster wa-gap-xs">
        <!-- Our own drawer toggle: supplying [data-toggle-nav] suppresses <wa-page>'s built-in
             hamburger, which would otherwise render on its own row outside this bar. -->
        <wa-button data-toggle-nav appearance="plain" size="small" class="wa-mobile-only">
          <wa-icon name="bars" label="Open menu"></wa-icon>
        </wa-button>
        <RouterLink :to="{ name: 'chat' }" class="brand-link wa-cluster wa-gap-xs" data-drawer="close">
          <img src="/cortex-logo-color.png" alt="" class="brand-mark" />
          <h1 class="wordmark"><VelmaWordmark sweep /></h1>
        </RouterLink>
      </div>
      <div class="identity wa-cluster wa-gap-xs">
        <wa-select
          v-if="session.realIsAdmin"
          class="act-as-select"
          size="small"
          label="View as:"
          :value="session.actingAs ?? ''"
          @change="onActAsChange"
        >
          <wa-option value="">Myself</wa-option>
          <wa-option v-for="t in actAsTargets" :key="t.email" :value="t.email">
            {{ t.email }}
          </wa-option>
        </wa-select>
        <span>{{ session.displayName }}</span>
        <wa-avatar :initials="initials" :label="session.displayName"></wa-avatar>
      </div>
    </header>

    <nav slot="navigation" class="wa-stack wa-gap-2xs">
      <div class="chat-nav-row wa-cluster wa-gap-2xs">
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'chat' }"
          data-drawer="close"
        >
          <wa-icon name="comments"></wa-icon>
          Chat
        </RouterLink>
        <wa-button
          class="new-chat-btn"
          appearance="plain"
          size="small"
          @click="router.push({ name: 'chat' })"
          data-drawer="close"
        >
          <wa-icon name="plus" label="New chat"></wa-icon>
        </wa-button>
      </div>

      <!-- The last conversations as children of Chat. Ten rows tall, then only this list
           scrolls -- the Chat link and the rest of the nav stay put. -->
      <div v-if="conversations.list.length" class="conv-tree" aria-label="Conversations">
        <div v-for="c in conversations.list" :key="c.id" class="conv-row wa-split">
          <RouterLink
            class="conv-link"
            :to="{ name: 'chat', params: { id: c.id } }"
            data-drawer="close"
          >
            {{ c.title || "Untitled" }}
          </RouterLink>
          <wa-button
            class="conv-trash"
            appearance="plain"
            size="small"
            @click="removeConversation(c.id)"
          >
            <wa-icon name="trash-can" label="Remove from history"></wa-icon>
          </wa-button>
        </div>
      </div>

      <!-- Every Velma User can search the knowledge base and request a removal; only the
           Pending approvals / History tabs inside gate themselves on Admin/Manager access. -->
      <RouterLink
        class="nav-link wa-cluster wa-gap-xs"
        :to="{ name: 'deleteKnowledge' }"
        data-drawer="close"
      >
        <wa-icon name="trash-can"></wa-icon>
        Flag for removal
      </RouterLink>

      <!-- Every Velma User can submit new knowledge; only Admins/Managers decide on it, on the
           Addition requests page below. -->
      <RouterLink
        class="nav-link wa-cluster wa-gap-xs"
        :to="{ name: 'upload' }"
        data-drawer="close"
      >
        <wa-icon name="file-circle-plus"></wa-icon>
        Add knowledge
      </RouterLink>

      <RouterLink class="nav-link wa-cluster wa-gap-xs" :to="{ name: 'about' }" data-drawer="close">
        <wa-icon name="circle-info"></wa-icon>
        About Velma
      </RouterLink>

      <!-- Manager surface: reviewing what people are asking within their assigned topics and
           actioning gaps/feedback. Admins get everything a Manager sees plus the Admin group
           below; a plain Velma User sees neither group. -->
      <div
        v-if="session.isAdmin || session.isManager"
        role="group"
        aria-labelledby="nav-manager-label"
        class="wa-stack wa-gap-2xs nav-role-group"
      >
        <small id="nav-manager-label" class="eyebrow">Manager</small>
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'assigned' }"
          data-drawer="close"
        >
          <wa-icon name="inbox"></wa-icon>
          Manager Inbox
          <wa-badge v-if="adminCounts.assignedPending" class="nav-badge" variant="warning">
            {{ adminCounts.assignedPending }}
          </wa-badge>
        </RouterLink>
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'filledGaps' }"
          data-drawer="close"
        >
          <wa-icon name="check-double"></wa-icon>
          Filled Gaps
        </RouterLink>
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'questionFeed' }"
          data-drawer="close"
        >
          <wa-icon name="comments"></wa-icon>
          Question Feed
        </RouterLink>
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'topicInsights' }"
          data-drawer="close"
        >
          <wa-icon name="chart-column"></wa-icon>
          Topic Insights
        </RouterLink>
        <!-- Here rather than beside Atlassian Data in the Admin group: Managers are the SME-like
             role, checking that their department's files in the Egnyte folder were picked up. -->
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'egnyteData' }"
          data-drawer="close"
        >
          <wa-icon name="folder-open"></wa-icon>
          Egnyte Data
        </RouterLink>
      </div>

      <!-- Admin surface: everything a Manager can't do. Kept as its own group with the same
           eyebrow-header pattern so the two role scopes are visually distinct at a glance,
           and so a Manager who never sees these links doesn't see an empty section header.

           Split into three bands by what a visit is *for*, because a flat list of eleven links
           made no distinction between running the product (Dashboard, Users, MCP) and the two
           standing workflows -- triaging what people asked, and curating what Velma knows.
           Queries and Knowledgebase are nested inside this group rather than sitting alongside
           it, so the whole surface stays behind one isAdmin check and a Manager can't be shown
           a bare subheading with nothing under it. -->
      <div
        v-if="session.isAdmin"
        role="group"
        aria-labelledby="nav-admin-label"
        class="wa-stack wa-gap-2xs nav-role-group"
      >
        <small id="nav-admin-label" class="eyebrow">Admin</small>
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'dashboard' }"
          data-drawer="close"
        >
          <wa-icon name="gauge-high"></wa-icon>
          Dashboard
        </RouterLink>
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'people' }"
          data-drawer="close"
        >
          <wa-icon name="user-shield"></wa-icon>
          Users
        </RouterLink>
        <RouterLink
          class="nav-link wa-cluster wa-gap-xs"
          :to="{ name: 'mcp' }"
          data-drawer="close"
        >
          <wa-icon name="plug"></wa-icon>
          MCP endpoint
        </RouterLink>

        <!-- What people asked, and what Velma got wrong: the read-and-triage loop. -->
        <div role="group" aria-labelledby="nav-queries-label" class="wa-stack wa-gap-2xs">
          <small id="nav-queries-label" class="eyebrow nav-subgroup-label">Queries</small>
          <RouterLink
            class="nav-link wa-cluster wa-gap-xs"
            :to="{ name: 'queryTags' }"
            data-drawer="close"
          >
            <wa-icon name="tags"></wa-icon>
            Query tags
          </RouterLink>
          <RouterLink
            class="nav-link wa-cluster wa-gap-xs"
            :to="{ name: 'log' }"
            data-drawer="close"
          >
            <wa-icon name="clock-rotate-left"></wa-icon>
            Query log
          </RouterLink>
          <RouterLink
            class="nav-link wa-cluster wa-gap-xs"
            :to="{ name: 'gaps' }"
            data-drawer="close"
          >
            <wa-icon name="puzzle-piece"></wa-icon>
            Knowledge gaps
          </RouterLink>
          <RouterLink
            class="nav-link wa-cluster wa-gap-xs"
            :to="{ name: 'feedback' }"
            data-drawer="close"
          >
            <wa-icon name="thumbs-up"></wa-icon>
            Feedback
          </RouterLink>
          <RouterLink
            class="nav-link wa-cluster wa-gap-xs"
            :to="{ name: 'outOfScope' }"
            data-drawer="close"
          >
            <wa-icon name="signs-post"></wa-icon>
            Out of scope
          </RouterLink>
        </div>

        <!-- What Velma knows, and what is being added to or removed from it. -->
        <div role="group" aria-labelledby="nav-knowledgebase-label" class="wa-stack wa-gap-2xs">
          <small id="nav-knowledgebase-label" class="eyebrow nav-subgroup-label">
            Knowledgebase
          </small>
          <RouterLink
            class="nav-link wa-cluster wa-gap-xs"
            :to="{ name: 'additionRequests' }"
            data-drawer="close"
          >
            <wa-icon name="list-check"></wa-icon>
            Addition requests
            <wa-badge v-if="adminCounts.additionPending" class="nav-badge" variant="warning">
              {{ adminCounts.additionPending }}
            </wa-badge>
          </RouterLink>
          <RouterLink
            class="nav-link wa-cluster wa-gap-xs"
            :to="{ name: 'removalRequests' }"
            data-drawer="close"
          >
            <wa-icon name="clipboard-check"></wa-icon>
            Removal requests
            <wa-badge v-if="adminCounts.removalPending" class="nav-badge" variant="warning">
              {{ adminCounts.removalPending }}
            </wa-badge>
          </RouterLink>
          <RouterLink
            class="nav-link wa-cluster wa-gap-xs"
            :to="{ name: 'atlassianData' }"
            data-drawer="close"
          >
            <wa-icon name="arrows-rotate"></wa-icon>
            Atlassian Data
          </RouterLink>
        </div>
      </div>
    </nav>

    <div slot="navigation-footer" class="wa-stack wa-gap-2xs">
      <wa-divider></wa-divider>
      <wa-button appearance="plain" size="small" @click="signOut()">
        <wa-icon slot="start" name="arrow-right-from-bracket"></wa-icon>
        Sign out
      </wa-button>
    </div>

    <main>
      <wa-callout v-if="session.actingAs" variant="warning" class="act-as-banner">
        <wa-icon slot="icon" name="eye"></wa-icon>
        <div class="wa-cluster wa-gap-s wa-align-items-center">
          <span>
            <strong>Viewing as {{ session.actingAs }}.</strong>
            Actions affect their queue.
          </span>
          <wa-button appearance="outlined" size="small" @click="stopActingAs">Stop</wa-button>
        </div>
      </wa-callout>
      <wa-callout v-if="session.error" variant="danger">
        <wa-icon slot="icon" name="triangle-exclamation"></wa-icon>
        <strong>Couldn't load your account.</strong>
        {{ session.error }}
      </wa-callout>
      <RouterView v-if="!session.error" />
    </main>
  </wa-page>
  <div
    v-if="isDesktop"
    class="nav-resize-handle"
    :style="{ left: navWidth + 'px' }"
    @pointerdown="startNavResize"
  ></div>
  </template>
</template>
